import { useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks'
import type { Client } from '../../../client/protocol.ts'
import { reduceConnection, type ConnectionDisplay, type DevicePresence } from '../../../client/connection.ts'
import { h as dom, icon, requestError } from '../dom.ts'
import { keys } from '../ui/layers.ts'
import { activationCause, animate, type MotionCause } from '../ui/spring.ts'
import { PageOwner } from './owner.ts'
import { hasUnsent } from './unsent.ts'
import { destinations, routes, type Route, type Destination } from './routes.ts'
import { acquireOverview, type SessionsOverview } from '../sessions/overview.ts'
import { sessionRow } from '../../../client/session-row.ts'
import { groupHeadings, agentWords } from '../../../client/status.ts'
import type { SessionsDisplay } from '../sessions/display.ts'
import { Glyph } from '../ui/icon.tsx'
import { UIProvider } from '../ui/scope.tsx'
import { duration } from '../ui/tokens.ts'

export interface Pane {
  el: HTMLElement
  setActive?(active: boolean): void
  scrollToTop?(): void
  select?(key?: string): void
  focus?(): void
  adjacent?(step: 1 | -1): string | undefined
  shortcut?(event: KeyboardEvent): void
}
export interface Entry { id: number; route: Route; owner: PageOwner; pane?: Pane }
export type Factory = (route: Route, owner: PageOwner) => Promise<Pane>

function reloadApp() {
  if (!hasUnsent() || confirm('Reload the app? Text and attachments you haven’t sent will be removed.')) location.reload()
}

function Legacy({ entry, factory, word, selected }: { entry: Entry; factory: Factory; word: string; selected?: string }) {
  const host = useRef<HTMLDivElement>(null)
  const selection = useRef(selected)
  selection.current = selected
  useLayoutEffect(() => {
    entry.owner.element = host.current!
    const load = () => void factory(entry.route, entry.owner).then(pane => {
      if (entry.owner.disposed) return
      entry.pane = pane
      pane.setActive?.(entry.owner.active)
      host.current!.append(pane.el)
      if (pane.scrollToTop) entry.owner.scrollToTop = () => pane.scrollToTop!()
      pane.select?.(selection.current)
      if (entry.owner.active) pane.focus?.()
    }).catch(() => {
      if (entry.owner.disposed) return
      const notice = requestError('This page couldn’t load', () => {
        notice.remove()
        load()
      })
      notice.append(dom('button.text-link', { type: 'button', onclick: reloadApp }, 'Reload'))
      host.current!.append(notice)
    })
    load()
    return () => entry.owner.dispose()
  }, [])
  return <>
    <div class="shell-connection type-caption" role="status" hidden={!word}>{word}</div>
    <div ref={host} class="legacy-host" />
  </>
}

function pageTitle(entry: Entry) {
  const kind = entry.route.kind
  return entry.owner.title || destinations.find(d => d[0] === kind)?.[1] || (kind === 'new' ? 'New session' : kind[0].toUpperCase() + kind.slice(1))
}

function NavIcon({ glyph }: { glyph: string }) {
  return <span class="nav-icon" ref={element => { if (element && !element.firstChild) element.append(icon(glyph, 24)) }} />
}

function PreviewRow({ display, sessionRef, selected, current }: { display: SessionsDisplay; sessionRef: string; selected?: string; current: boolean }) {
  const value = display.rows.item(sessionRef).value
  if (!value) return null
  const row = value.row, words = sessionRow(row, Date.now(), { showBackend: false })
  return <a class="shell-preview-row" data-ref={sessionRef} href={'#/s/' + encodeURIComponent(sessionRef)} aria-current={selected === sessionRef ? 'page' : undefined}
    aria-disabled={value.removed || undefined} onClick={event => { if (!display.canOpen(sessionRef)) event.preventDefault() }}>
    <Glyph status={words.status} still={!current || value.removed} />
    <span class="shell-preview-text"><span class="type-subhead-strong" title={row.title}>{row.title || 'Untitled session'}</span>
      <span class="type-caption secondary"><span data-tone={words.tone}>{words.phrase}</span>{row.cwd ? ` · ${row.cwd.split(/[\\/]/).filter(Boolean).at(-1)}` : ''}</span></span>
  </a>
}

function PreviewSection({ title, count, display, selected, running = false }: { title: string; count?: number; display: SessionsDisplay; selected?: string; running?: boolean }) {
  const group = running ? 'running-unattended' : 'needs-you'
  const structure = display.structure.value, current = count !== undefined
  const content = useMemo(() => structure.flatMap(entry => entry.kind === 'row' && entry.group === group
    ? [<PreviewRow key={entry.ref} display={display} sessionRef={entry.ref} selected={selected} current={current} />] : []), [structure, group, display, selected, current])
  if (!content.length) return null
  return <section class={'shell-triage-section ' + (running ? 'shell-triage-running' : 'shell-triage-needs')} aria-label={`${title}, ${count === undefined ? 'saved sessions' : count}`} data-current={count !== undefined}>
    <h2 class="shell-triage-heading type-footnote-strong">{running ? <a href="#/running">{title}</a> : <span>{title}</span>}<span class="type-caption secondary">{count ?? 'Saved'}</span></h2>
    {content}
    {running && <div class="shell-triage-more type-footnote">{count !== undefined && count > content.length && <a href="#/running">{count - content.length} more at work</a>}</div>}
  </section>
}

function Sidebar({ overview, owner, selected, showError }: { overview: SessionsOverview; owner: PageOwner; selected?: string; showError: boolean }) {
  const display = overview.preview
  const cancel = useRef(() => {})
  const hold = (reason: 'pointer' | 'focus', on: boolean) => {
    cancel.current()
    display.dispatch({ type: 'hold', reason, on, now: Date.now() })
    if (!on) cancel.current = owner.timeout(() => display.dispatch({ type: 'tick', now: Date.now() }), duration['tap-guard'])
  }
  useLayoutEffect(() => {
    const release = () => {
      cancel.current()
      for (const reason of ['pointer', 'focus'] as const) display.dispatch({ type: 'hold', reason, on: false, now: 0 })
      display.dispatch({ type: 'tick', now: Date.now() })
    }
    const activity = owner.onActive(active => { if (!active) release() })
    return () => { activity(); release() }
  }, [display, owner])
  return <div class="shell-triage"
    onPointerEnter={() => hold('pointer', true)} onPointerLeave={() => hold('pointer', false)} onFocusIn={() => hold('focus', true)}
    onFocusOut={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) hold('focus', false) }}>
    <PreviewSection title={groupHeadings[0].heading} count={overview.counts.value.all.needs} display={display} selected={selected} />
    <PreviewSection title={agentWords.running} count={overview.counts.value.all.working} display={display} selected={selected} running />
    <div class="shell-triage-error type-footnote" role={showError && overview.error.value ? 'status' : undefined}>{showError && overview.error.value && <><p title={overview.error.value}>{overview.error.value}</p><button type="button" class="text-link" onClick={overview.retry}>Try again</button></>}</div>
  </div>
}

export function Shell({ client, factory, presence }: { client: Client; factory: Factory; presence?: DevicePresence }) {
  const stacks = useRef(new Map<Destination, Entry[]>())
  const sequence = useRef(0)
  const shellOwner = useRef(new PageOwner()).current
  const navigation = useRef<{ hash: string; cause: MotionCause }>()
  const visiblePages = useRef(new Set<number>())
  const connection = useRef<{ page: number; display?: ConnectionDisplay }>()
  const [route, setRoute] = useState(() => routes(location.hash))
  const [desktop, setDesktop] = useState(matchMedia('(min-width: 1024px)').matches)
  const [documentShown, setDocumentShown] = useState(!document.hidden)
  const [word, setWord] = useState('')
  const overviewRef = useRef<SessionsOverview>()
  if (!overviewRef.current) {
    shellOwner.setActive(documentShown)
    overviewRef.current = acquireOverview(client, shellOwner, { sidebar: desktop })
  }
  const overview = overviewRef.current
  const destination = route[0].destination

  function reconcile(next: Route[]) {
    const stack = stacks.current.get(next[0].destination) ?? []
    let common = 0
    while (common < stack.length && common < next.length && stack[common].route.path === next[common].path) common++
    // Legacy's unmount cleanup owns disposal, after this component has rendered.
    // A pane can own independent Preact roots; disposing them here would reenter the renderer.
    stack.splice(common)
    for (const item of next.slice(common)) stack.push({ id: sequence.current++, route: item, owner: new PageOwner() })
    stacks.current.set(next[0].destination, stack)
  }
  reconcile(route)
  const current = stacks.current.get(destination)!
  const top = current.at(-1)!
  const master = current.find(entry => entry.route.kind === 'outputs' && !entry.route.ref) ?? current[0]
  const all = [...stacks.current.values()].flat()
  const shown = (entry: Entry) => entry.route.destination === destination && (entry === top || desktop && entry === master)

  useLayoutEffect(() => {
    shellOwner.setActive(documentShown)
    overview.update({ sidebar: desktop })
  }, [desktop, documentShown])

  useLayoutEffect(() => {
    master.pane?.select?.(top.route.output ?? top.route.ref)
    const visible = new Set<number>()
    for (const entry of all) {
      // One lifecycle entry: a page is active only while its route shows it and the document is visible.
      const routed = current.includes(entry) && (entry === top || desktop && entry === master), active = documentShown && routed
      entry.owner.setActive(active)
      entry.pane?.setActive?.(active)
      if (routed) {
        visible.add(entry.id)
        const element = entry.owner.element?.closest('.shell-page')
        if (element && !visiblePages.current.has(entry.id)) animate(element, [{ opacity: 0 }, { opacity: 1 }], { owner: entry.owner, cause: navigation.current?.hash === entry.route.path ? navigation.current.cause : 'unknown' })
      }
    }
    visiblePages.current = visible
    navigation.current = undefined
  }, [route, desktop, documentShown])

  useLayoutEffect(() => {
    const media = matchMedia('(min-width: 1024px)')
    shellOwner.listen(window, 'hashchange', () => {
      if (navigation.current?.hash !== location.hash) navigation.current = undefined
      window.dispatchEvent(new Event('cx:navigation'))
      setRoute(routes(location.hash))
    })
    shellOwner.listen(media, 'change', () => setDesktop(media.matches))
    shellOwner.listen(document, 'visibilitychange', () => setDocumentShown(!document.hidden))
    // The inset is the part of the layout viewport the visual viewport does not show; it judges nothing.
    const viewport = () => {
      const v = visualViewport
      const inset = v ? Math.max(0, innerHeight - (v.offsetTop + v.height * v.scale)) : 0
      document.documentElement.style.setProperty('--shell-keyboard-inset', inset + 'px')
    }
    if (visualViewport) {
      shellOwner.listen(visualViewport, 'resize', viewport)
      shellOwner.listen(visualViewport, 'scroll', viewport)
    }
    viewport()
    keys(shellOwner, event => {
      const key = event.key.toLowerCase()
      if ((event.metaKey || event.ctrlKey) && ['n', 'k'].includes(key)) {
        location.hash = key === 'n' ? '#/new' : '#/'
        if (key === 'k') {
          shellOwner.frame(() => {
            if (location.hash === '#/') stacks.current.get('sessions')?.[0].owner.element?.querySelector<HTMLInputElement>('.search-input')?.focus()
          })
        }
        return true
      }
      const active = stacks.current.get(routes(location.hash)[0].destination)!
      const list = active.findLast(entry => entry.route.kind === 'outputs') ?? active[0]
      list.pane?.shortcut?.(event)
      if (event.defaultPrevented) return true
      const next = ['[', ']'].includes(event.key) ? list.pane?.adjacent?.(event.key === ']' ? 1 : -1) : undefined
      if (next) {
        location.hash = '#/s/' + encodeURIComponent(next)
        return true
      }
      if (event.key === 'Escape' && active.length > 1) {
        location.hash = active.at(-2)!.route.path
        return true
      }
    })
    return () => {
      shellOwner.dispose()
      for (const entry of [...stacks.current.values()].flat()) entry.owner.dispose()
      document.documentElement.style.removeProperty('--shell-keyboard-inset')
    }
  }, [])

  useLayoutEffect(() => {
    let cancel = () => {}
    if (connection.current?.page !== top.id) connection.current = { page: top.id }
    const update = () => {
      cancel()
      const input = { state: client.state, online: navigator.onLine, presence, view: top.owner.view, reason: client.stopped }
      const display = connection.current!.display = reduceConnection(connection.current!.display, input, Date.now())
      setWord(display.text)
      document.title = (display.text ? display.text + ' · ' : '') + pageTitle(top) + ' · cx-next'
      if (display.deadline !== undefined) cancel = shellOwner.timeout(update, Math.max(0, display.deadline - Date.now()))
    }
    const unsubscribe = client.subscribe(update)
    const unview = top.owner.onView(update)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    update()
    return () => {
      cancel()
      unsubscribe()
      unview()
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [top.id, presence])

  const select = (id: Destination) => (event: MouseEvent) => {
    if (event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return
    event.preventDefault()
    if (id !== destination) {
      navigation.current = { hash: (event.currentTarget as HTMLAnchorElement).hash, cause: activationCause(event) }
      location.hash = navigation.current.hash
    } else {
      current[0].owner.scrollToTop()
    }
  }
  return <div class={'app shell-app' + (current.length > 1 ? ' has-detail' : '')} data-tab={destination}>
    <nav class="nav" aria-label="Destinations">
      <div class="shell-destinations">
      {destinations.map(([id, title, path, glyph]) =>
        <a class="nav-item" href={stacks.current.get(id)?.at(-1)?.route.path ?? path} aria-current={id === destination ? 'true' : 'false'} aria-label={title + (id === 'catchup' && overview.counts.value.all.needs ? `, ${groupHeadings[0].heading} ${overview.counts.value.all.needs}` : '')} title={title} onClick={select(id)}>
          <span class="shell-nav-mark"><NavIcon glyph={glyph} />{id === 'catchup' && !!overview.counts.value.all.needs && <span class="nav-badge type-caption">{overview.counts.value.all.needs}</span>}</span>
          <span class="nav-label type-caption">{title}</span>
        </a>)}
      </div>
      {desktop && <UIProvider owner={shellOwner}><Sidebar overview={overview} owner={shellOwner} selected={top.route.ref} showError={destination !== 'sessions'} /></UIProvider>}
      <a class="nav-item shell-settings" href="#/settings" aria-label="Settings">
        <NavIcon glyph="tune" />
        <span class="nav-label type-caption">Settings</span>
      </a>
    </nav>
    {all.map(entry =>
      <section key={entry.id} class={'shell-page ' + (entry === master ? 'shell-master' : 'shell-detail')} data-route={entry.route.path}
        data-page-active={String(shown(entry))} hidden={!shown(entry)} inert={!shown(entry)} tabIndex={-1}>
        <Legacy entry={entry} factory={factory} word={entry === top ? word : ''} selected={top.route.output ?? top.route.ref} />
      </section>)}
    {desktop && current.length === 1 && !['accounts', 'devices'].includes(destination) && <div class="shell-empty pick">Pick a session</div>}
  </div>
}
